import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { endpoints, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Save, MapPin, Eye } from "lucide-react";
import { Link } from "react-router-dom";
import ImageUploader from "@/components/ImageUploader";

const SOCIALS = ["Instagram", "TikTok", "YouTube", "X / Twitter", "Facebook", "Other"];

export default function VenueProfileEditor() {
    const { venue } = useOutletContext();
    const [form, setForm] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!venue?.id) return;
        endpoints.venuePublic(venue.id).then((d) => {
            const v = d.venue;
            setForm({
                name: v.name || "", description: v.description || "",
                address: v.address || "", city: v.city || "", country: v.country || "",
                image_url: v.image_url || "", cover_url: v.cover_url || "",
                website: v.website || "", capacity: v.capacity || 0,
                social_links: (v.social_links || []).map((s) => ({ ...s })),
            });
        }).catch(() => setForm({ name: venue.name, description: "", address: "", city: "", country: "", image_url: "", cover_url: "", website: "", capacity: 0, social_links: [] }));
    }, [venue?.id]);

    if (!form) return <div className="p-8 font-mono text-xs text-[#666]" data-testid="venue-profile-loading">Loading…</div>;
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const save = async () => {
        setSaving(true);
        try {
            await endpoints.venueUpdateProfile(venue.id, form);
            toast.success("Venue page saved");
        } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
    };

    const addSocial = () => set("social_links", [...(form.social_links || []), { platform: "Instagram", url: "" }]);
    const rmSocial = (i) => set("social_links", form.social_links.filter((_, idx) => idx !== i));
    const updSocial = (i, patch) => set("social_links", form.social_links.map((s, idx) => idx === i ? { ...s, ...patch } : s));

    return (
        <div className="pm-fade" data-testid="venue-profile-editor">
            <header className="border-b border-[#2a2a2a] px-4 md:px-8 py-6 flex items-center justify-between flex-wrap gap-3 sticky top-0 bg-[#0a0a0a] z-30">
                <div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-[#666]">Venue page</div>
                    <h1 className="font-display font-black uppercase tracking-tighter text-3xl md:text-4xl">Edit public page</h1>
                </div>
                <button onClick={save} disabled={saving} className="pm-btn pm-btn-primary" data-testid="venue-profile-save">
                    <Save className="w-4 h-4 mr-2" /> {saving ? "Saving…" : "Save"}
                </button>
                <Link to={`/venues/${venue.id}`} target="_blank" rel="noopener noreferrer" className="pm-btn" data-testid="venue-profile-preview">
                    <Eye className="w-4 h-4 mr-2" /> Preview public page
                </Link>
            </header>

            <div className="px-4 md:px-8 py-6 space-y-6 max-w-4xl">
                <Section title="Basics">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <Field label="Venue name"><Input v={form.name} on={(v) => set("name", v)} testId="vp-name" /></Field>
                        <Field label="Capacity"><Input type="number" v={form.capacity} on={(v) => set("capacity", parseInt(v || 0))} testId="vp-capacity" /></Field>
                        <Field label="City"><Input v={form.city} on={(v) => set("city", v)} testId="vp-city" /></Field>
                        <Field label="Country"><Input v={form.country} on={(v) => set("country", v)} testId="vp-country" /></Field>
                    </div>
                    <Field label="Address"><Input v={form.address} on={(v) => set("address", v)} testId="vp-address" /></Field>
                    <Field label="Website"><Input v={form.website} on={(v) => set("website", v)} placeholder="https://…" testId="vp-website" /></Field>
                    <Field label="Description"><textarea rows={4} value={form.description} onChange={(e) => set("description", e.target.value)} className="w-full bg-transparent border border-[#2a2a2a] px-3 py-2 focus:border-[#ff4400] outline-none" data-testid="vp-description" /></Field>
                </Section>

                <Section title="Images">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <Field label="Profile image">
                            <ImageUploader value={form.image_url} onChange={(v) => set("image_url", v)} scope="admin" aspect="square" testId="vp-image" />
                        </Field>
                        <Field label="Cover image">
                            <ImageUploader value={form.cover_url} onChange={(v) => set("cover_url", v)} scope="admin" aspect="video" testId="vp-cover" />
                        </Field>
                    </div>
                </Section>

                <Section title="Social links">
                    <div className="space-y-2">
                        {(form.social_links || []).map((s, i) => (
                            <div key={i} className="grid grid-cols-[140px_1fr_auto] gap-2 border border-[#2a2a2a] p-2">
                                <select value={s.platform} onChange={(e) => updSocial(i, { platform: e.target.value })} className="bg-transparent border border-[#2a2a2a] px-2 py-1 text-xs font-mono uppercase" data-testid={`vp-social-platform-${i}`}>
                                    {SOCIALS.map((p) => <option key={p} value={p} className="bg-[#141414]">{p}</option>)}
                                </select>
                                <Input v={s.url} on={(v) => updSocial(i, { url: v })} placeholder="https://…" testId={`vp-social-url-${i}`} />
                                <button onClick={() => rmSocial(i)} className="pm-btn text-[10px] text-[#ff3333] border-[#ff3333]" data-testid={`vp-social-remove-${i}`}>Remove</button>
                            </div>
                        ))}
                        <button onClick={addSocial} className="pm-btn text-xs" data-testid="vp-social-add">+ Add social link</button>
                    </div>
                </Section>
            </div>
        </div>
    );
}

function Section({ title, children }) {
    return (
        <section>
            <h2 className="font-display font-black uppercase text-xl mb-3">{title}</h2>
            <div className="space-y-3">{children}</div>
        </section>
    );
}
function Field({ label, children }) {
    return <label className="block"><div className="text-[10px] font-mono uppercase tracking-widest text-[#666] mb-1">{label}</div>{children}</label>;
}
function Input({ v, on, type = "text", placeholder, testId }) {
    return <input type={type} value={v ?? ""} onChange={(e) => on(e.target.value)} placeholder={placeholder} data-testid={testId} className="w-full bg-transparent border border-[#2a2a2a] px-3 py-2 focus:border-[#ff4400] outline-none text-sm" />;
}
